import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, Polygon, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'

import { 
  MapPin, Bell, Search, Filter, Building, Calendar, ExternalLink, 
  CheckCircle, AlertCircle, Home, Building2, Warehouse, TreePine, 
  ChevronDown, ChevronUp, Hash, X, Plus, Settings, RefreshCw, Loader2,
  Star, LogIn, LogOut, User, Heart, Save, Trash2, Mail, PenTool, Edit3, Megaphone
} from 'lucide-react'

// ============================================
// API SERVICE
// ============================================

const API_BASE = import.meta.env.VITE_API_URL || '/api'

const getToken = () => localStorage.getItem('auth_token')
const setToken = (token) => token ? localStorage.setItem('auth_token', token) : localStorage.removeItem('auth_token')

const authHeaders = () => {
  const token = getToken()
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

const api = {
  async getProjects(filters = {}) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') params.append(key, value)
    })
    const response = await fetch(`${API_BASE}/projects?${params}`)
    if (!response.ok) throw new Error('Failed to fetch projects')
    return response.json()
  },
  
  async getGemeenten() {
    const response = await fetch(`${API_BASE}/gemeenten`)
    if (!response.ok) throw new Error('Failed to fetch gemeenten')
    return response.json()
  },
  
  async getStatuses() {
    const response = await fetch(`${API_BASE}/statuses`)
    if (!response.ok) throw new Error('Failed to fetch statuses')
    return response.json()
  },
  
  async getStats() {
    const response = await fetch(`${API_BASE}/stats`)
    if (!response.ok) throw new Error('Failed to fetch stats')
    return response.json()
  },
  
  async triggerRefresh() {
    const response = await fetch(`${API_BASE}/admin/fetch`, {
      method: 'POST',
      headers: { 'x-admin-key': 'public-refresh' }
    })
    return response.json()
  },
  
  async login(email) {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    })
    if (!response.ok) throw new Error('Login failed')
    return response.json()
  },
  
  async verifyToken(token) {
    const response = await fetch(`${API_BASE}/auth/verify?token=${token}`)
    if (!response.ok) throw new Error('Verification failed')
    return response.json()
  },
  
  async getMe() {
    const response = await fetch(`${API_BASE}/auth/me`, { headers: authHeaders() })
    if (!response.ok) return null
    return response.json()
  },
  
  async logout() {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST', headers: authHeaders() })
    setToken(null)
  },
  
  async getFavorites() {
    const response = await fetch(`${API_BASE}/favorites`, { headers: authHeaders() })
    if (!response.ok) return []
    return response.json()
  },
  
  async getFavoriteIds() {
    const response = await fetch(`${API_BASE}/favorites/ids`, { headers: authHeaders() })
    if (!response.ok) return []
    return response.json()
  },
  
  async addFavorite(projectId, notes) {
    const response = await fetch(`${API_BASE}/favorites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ projectId, notes })
    })
    return response.json()
  },
  
  async removeFavorite(projectId) {
    const response = await fetch(`${API_BASE}/favorites/${projectId}`, {
      method: 'DELETE',
      headers: authHeaders()
    })
    return response.json()
  },
  
  async getSavedFilters() {
    const response = await fetch(`${API_BASE}/filters`, { headers: authHeaders() })
    if (!response.ok) return []
    return response.json()
  },
  
  async saveFilter(name, filters, isDefault = false) {
    const response = await fetch(`${API_BASE}/filters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ name, filters, isDefault })
    })
    return response.json()
  },
  
  async deleteFilter(filterId) {
    const response = await fetch(`${API_BASE}/filters/${filterId}`, {
      method: 'DELETE',
      headers: authHeaders()
    })
    return response.json()
  },
  
  async getSubscriptions() {
    const response = await fetch(`${API_BASE}/subscriptions`, { headers: authHeaders() })
    if (!response.ok) return []
    return response.json()
  },
  
  async createSubscription(data) {
    const response = await fetch(`${API_BASE}/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data)
    })
    return response.json()
  },
  
  async deleteSubscription(id) {
    const response = await fetch(`${API_BASE}/subscriptions/${id}`, {
      method: 'DELETE',
      headers: authHeaders()
    })
    return response.json()
  }
}

// ============================================
// CONFIGS
// ============================================

const typeConfig = {
  stedenbouwkundig: { label: 'Stedenbouwkundig', bg: '#dbeafe', color: '#1e40af' },
  milieu: { label: 'Milieu', bg: '#dcfce7', color: '#166534' },
  verkaveling: { label: 'Verkaveling', bg: '#fef3c7', color: '#b45309' },
  kleinhandel: { label: 'Kleinhandel', bg: '#f3e8ff', color: '#7c3aed' },
  vegetatie: { label: 'Vegetatie', bg: '#d1fae5', color: '#059669' }
}

const woningTypeConfig = {
  eengezins: { label: 'Eengezinswoning', icon: Home, color: '#4caf50' },
  meergezins: { label: 'Meergezinswoning', icon: Building2, color: '#2196f3' },
  bijgebouw: { label: 'Bijgebouw', icon: Warehouse, color: '#ff9800' },
  commercieel: { label: 'Commercieel', icon: Building, color: '#9c27b0' },
  industrieel: { label: 'Industrieel', icon: Building, color: '#607d8b' },
  landbouw: { label: 'Landbouw', icon: TreePine, color: '#8bc34a' },
  publiek: { label: 'Publiek/Maatschappelijk', icon: Building, color: '#00bcd4' },
  infrastructuur: { label: 'Infrastructuur', icon: MapPin, color: '#795548' },
  reclame: { label: 'Reclame/Publiciteit', icon: Megaphone, color: '#e91e63' },
  recreatie: { label: 'Recreatie', icon: TreePine, color: '#00acc1' },
  groen_landschap: { label: 'Groen/Landschap', icon: TreePine, color: '#2e7d32' },
  milieu: { label: 'Milieu', icon: AlertCircle, color: '#ff5722' },
  bouw_overig: { label: 'Bouw (overig)', icon: Building, color: '#78909c' },
  overig: { label: 'Overig', icon: MapPin, color: '#9e9e9e' }
}

const actieTypeConfig = {
  // Bouw acties
  nieuwbouw: { label: 'Nieuwbouw', color: '#4caf50' },
  herbouw: { label: 'Herbouw', color: '#8bc34a' },
  verbouwing: { label: 'Verbouwing/Renovatie', color: '#ff9800' },
  sloop: { label: 'Sloop', color: '#f44336' },
  regularisatie: { label: 'Regularisatie', color: '#9c27b0' },
  functiewijziging: { label: 'Functiewijziging', color: '#00bcd4' },
  verkaveling: { label: 'Verkaveling', color: '#3f51b5' },
  plaatsing: { label: 'Plaatsing/Aanleg', color: '#795548' },
  opsplitsing: { label: 'Opsplitsing', color: '#1565c0' },
  samenvoeging: { label: 'Samenvoeging', color: '#6a1b9a' },
  // Groen/landschap acties
  ontbossing: { label: 'Ontbossing/Kappen', color: '#33691e' },
  reliefwijziging: { label: 'Reliëfwijziging', color: '#5d4037' },
  waterhuishouding: { label: 'Waterhuishouding', color: '#0277bd' },
  graslandwijziging: { label: 'Graslandwijziging', color: '#558b2f' },
  groenwerk: { label: 'Groenwerk', color: '#2e7d32' },
  overig: { label: 'Overig', color: '#9e9e9e' }
}

// ============================================
// MAP COMPONENTS
// ============================================

function MapController({ center, zoom }) {
  const map = useMap()
  useEffect(() => {
    if (center) map.setView(center, zoom)
  }, [center, zoom, map])
  return null
}

function DrawControl({ onPolygonCreated, onPolygonDeleted, polygon }) {
  const map = useMap()
  const drawnItemsRef = useRef(null)
  const drawControlRef = useRef(null)
  
  useEffect(() => {
    if (!map) return
    
    // Use window.L (from CDN) which has the Draw plugin
    const LDraw = window.L
    
    // Check if L.Draw exists
    if (!LDraw || !LDraw.Control || !LDraw.Control.Draw) {
      console.error('Leaflet Draw not loaded! Make sure CDN scripts are loaded.')
      return
    }
    
    console.log('Initializing DrawControl with CDN Leaflet...')
    
    // Create feature group for drawn items using window.L
    const drawnItems = new LDraw.FeatureGroup()
    map.addLayer(drawnItems)
    drawnItemsRef.current = drawnItems
    
    // Add existing polygon if any
    if (polygon && polygon.length > 0) {
      const latlngs = polygon.map(p => [p[0], p[1]])
      const layer = LDraw.polygon(latlngs, { color: '#3b82f6', fillOpacity: 0.2 })
      drawnItems.addLayer(layer)
    }
    
    // Create draw control with explicit options
    const drawControl = new LDraw.Control.Draw({
      position: 'topright',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: '#3b82f6', fillOpacity: 0.2, weight: 2 }
        },
        polyline: false,
        circle: false,
        rectangle: {
          shapeOptions: { color: '#3b82f6', fillOpacity: 0.2, weight: 2 }
        },
        marker: false,
        circlemarker: false
      },
      edit: {
        featureGroup: drawnItems,
        remove: true
      }
    })
    
    map.addControl(drawControl)
    drawControlRef.current = drawControl
    console.log('DrawControl added to map')
    
    // Handle polygon/rectangle creation
    map.on(LDraw.Draw.Event.CREATED, (e) => {
      console.log('Shape created:', e.layerType)
      drawnItems.clearLayers()
      drawnItems.addLayer(e.layer)
      
      let coords
      if (e.layerType === 'rectangle') {
        const bounds = e.layer.getBounds()
        coords = [
          [bounds.getNorth(), bounds.getWest()],
          [bounds.getNorth(), bounds.getEast()],
          [bounds.getSouth(), bounds.getEast()],
          [bounds.getSouth(), bounds.getWest()]
        ]
      } else {
        const latlngs = e.layer.getLatLngs()[0]
        coords = latlngs.map(ll => [ll.lat, ll.lng])
      }
      onPolygonCreated(coords)
    })
    
    // Handle deletion
    map.on(LDraw.Draw.Event.DELETED, () => {
      console.log('Shape deleted')
      onPolygonDeleted()
    })
    
    return () => {
      map.off(LDraw.Draw.Event.CREATED)
      map.off(LDraw.Draw.Event.DELETED)
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current)
      }
      if (drawnItemsRef.current) {
        map.removeLayer(drawnItemsRef.current)
      }
    }
  }, [map, onPolygonCreated, onPolygonDeleted])
  
  return null
}

// ============================================
// MODALS
// ============================================

function LoginModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await api.login(email)
      setSent(true)
    } catch (err) {
      setError('Kon login link niet versturen. Probeer later opnieuw.')
    }
    setLoading(false)
  }
  
  if (!isOpen) return null
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="p-6 bg-gray-800 text-white rounded-t-xl">
          <h2 className="text-xl font-bold flex items-center gap-2"><LogIn size={20} /> Inloggen</h2>
          <p className="text-sm text-gray-300 mt-1">Log in om favorieten en filters te bewaren</p>
        </div>
        <div className="p-6">
          {sent ? (
            <div className="text-center py-4">
              <Mail size={48} className="mx-auto text-green-500 mb-3" />
              <p className="text-green-700 font-medium">Login link verzonden!</p>
              <p className="text-sm text-gray-500 mt-2">Check je email en klik op de link om in te loggen.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <p className="text-sm text-gray-600 mb-4">Vul je email in en we sturen je een link om in te loggen. Geen wachtwoord nodig!</p>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-3 py-2 border rounded-lg mb-4" placeholder="jouw@email.be" />
              <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                Login link versturen
              </button>
            </form>
          )}
        </div>
        <div className="px-6 py-3 bg-gray-50 rounded-b-xl text-right">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Sluiten</button>
        </div>
      </div>
    </div>
  )
}

function SaveFilterModal({ isOpen, onClose, filters, onSave }) {
  const [name, setName] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [loading, setLoading] = useState(false)
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    await onSave(name, filters, isDefault)
    setLoading(false)
    setName('')
    onClose()
  }
  
  if (!isOpen) return null
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b">
          <h2 className="text-lg font-bold flex items-center gap-2"><Save size={18} /> Filter opslaan</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Naam</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required className="w-full px-3 py-2 border rounded-lg" placeholder="Mijn filter" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="isDefault" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} className="rounded" />
            <label htmlFor="isDefault" className="text-sm text-gray-600">Standaard filter bij openen</label>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs font-medium text-gray-500 mb-2">Huidige filters:</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(filters).filter(([k, v]) => v && v !== 'all').map(([key, value]) => (
                <span key={key} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{key}: {value}</span>
              ))}
              {Object.entries(filters).filter(([k, v]) => v && v !== 'all').length === 0 && (
                <span className="text-xs text-gray-400">Geen filters actief</span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Annuleren</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function SubscriptionModal({ isOpen, onClose, user, polygon, statuses, onSave }) {
  const [name, setName] = useState('')
  const [gemeente, setGemeente] = useState('all')
  const [vergunningType, setVergunningType] = useState('all')
  const [status, setStatus] = useState('all')
  const [woningType, setWoningType] = useState('all')
  const [actieType, setActieType] = useState('all')
  const [minUnits, setMinUnits] = useState('')
  const [loading, setLoading] = useState(false)
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    await onSave({
      email: user?.email,
      name: name || 'Mijn notificatie',
      gemeente,
      vergunningType,
      status,
      woningType,
      actieType,
      minUnits: minUnits ? parseInt(minUnits) : null,
      polygon: polygon && polygon.length > 0 ? polygon : null
    })
    setLoading(false)
    onClose()
  }
  
  if (!isOpen) return null
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 bg-amber-500 text-white rounded-t-xl">
          <h2 className="text-lg font-bold flex items-center gap-2"><Bell size={18} /> Notificatie instellen</h2>
          <p className="text-sm text-amber-100 mt-1">Ontvang een email bij nieuwe projecten</p>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {polygon && polygon.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <PenTool size={16} />
                <strong>Getekend gebied actief</strong> - Notificaties alleen voor projecten in dit gebied
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Naam notificatie</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="bv. Nieuwbouw Antwerpen" />
          </div>
          
          {(!polygon || polygon.length === 0) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gemeente</label>
              <select value={gemeente} onChange={e => setGemeente(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="all">Alle gemeenten</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Of teken een gebied op de kaart voor specifiekere notificaties</p>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vergunningstype</label>
              <select value={vergunningType} onChange={e => setVergunningType(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="all">Alle types</option>
                {Object.entries(typeConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="all">Alle statussen</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Woning type</label>
              <select value={woningType} onChange={e => setWoningType(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="all">Alle types</option>
                {Object.entries(woningTypeConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actie type</label>
              <select value={actieType} onChange={e => setActieType(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="all">Alle acties</option>
                {Object.entries(actieTypeConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minimum aantal units</label>
            <input type="number" value={minUnits} onChange={e => setMinUnits(e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="bv. 5" />
          </div>
          
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Annuleren</button>
            <button type="submit" disabled={loading || !user} className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
              {loading ? 'Opslaan...' : 'Notificatie aanmaken'}
            </button>
          </div>
          {!user && <p className="text-xs text-red-500 text-center">Je moet ingelogd zijn om notificaties te maken</p>}
        </form>
      </div>
    </div>
  )
}

// ============================================
// MAIN APP
// ============================================

export default function App() {
  const [projects, setProjects] = useState([])
  const [gemeenten, setGemeenten] = useState([])
  const [statuses, setStatuses] = useState([])
  const [stats, setStats] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  const [user, setUser] = useState(null)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [favoriteIds, setFavoriteIds] = useState(new Set())
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [bouwOnly, setBouwOnly] = useState(true) // Default: alleen bouw-gerelateerd
  
  const [savedFilters, setSavedFilters] = useState([])
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false)
  
  const [subscriptions, setSubscriptions] = useState([])
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [drawingPolygon, setDrawingPolygon] = useState(null)
  
  const [selectedGemeente, setSelectedGemeente] = useState('all')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedWoningType, setSelectedWoningType] = useState('all')
  const [selectedActieType, setSelectedActieType] = useState('all')
  const [minUnits, setMinUnits] = useState('')
  const [maxUnits, setMaxUnits] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true)
  
  const [selectedProject, setSelectedProject] = useState(null)
  const [mapCenter, setMapCenter] = useState([51.0, 4.0])
  const [mapZoom, setMapZoom] = useState(8)
  
  const [toast, setToast] = useState(null)
  
  const currentFilters = useMemo(() => ({
    gemeente: selectedGemeente, type: selectedType, status: selectedStatus, woningType: selectedWoningType,
    actieType: selectedActieType, minUnits, maxUnits, search: searchTerm
  }), [selectedGemeente, selectedType, selectedStatus, selectedWoningType, selectedActieType, minUnits, maxUnits, searchTerm])
  
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }, [])

  // Check for magic link token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (token) {
      api.verifyToken(token).then(data => {
        if (data.token) {
          setToken(data.token)
          setUser(data.user)
          showToast('Succesvol ingelogd!')
          window.history.replaceState({}, '', window.location.pathname)
        }
      }).catch(() => showToast('Login link ongeldig of verlopen', 'error'))
    }
  }, [showToast])

  // Check existing session
  useEffect(() => {
    if (getToken()) {
      api.getMe().then(data => {
        if (data) setUser(data)
        else setToken(null)
      })
    }
  }, [])

  // Load favorites/filters/subscriptions when user logs in
  useEffect(() => {
    if (user) {
      api.getFavoriteIds().then(ids => setFavoriteIds(new Set(ids)))
      api.getSavedFilters().then(filters => {
        setSavedFilters(filters)
        const defaultFilter = filters.find(f => f.is_default)
        if (defaultFilter?.filters) applyFilter(defaultFilter.filters)
      })
      api.getSubscriptions().then(setSubscriptions)
    } else {
      setFavoriteIds(new Set())
      setSavedFilters([])
      setSubscriptions([])
    }
  }, [user])

  const applyFilter = useCallback((filters) => {
    if (filters.gemeente) setSelectedGemeente(filters.gemeente)
    if (filters.type) setSelectedType(filters.type)
    if (filters.status) setSelectedStatus(filters.status)
    if (filters.woningType) setSelectedWoningType(filters.woningType)
    if (filters.actieType) setSelectedActieType(filters.actieType)
    if (filters.minUnits) setMinUnits(filters.minUnits)
    if (filters.maxUnits) setMaxUnits(filters.maxUnits)
    if (filters.search) setSearchTerm(filters.search)
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [projectsData, gemeentenData, statusesData, statsData] = await Promise.all([
        api.getProjects({
          gemeente: selectedGemeente, type: selectedType, status: selectedStatus, woningType: selectedWoningType,
          bouwOnly: bouwOnly,
          minUnits: minUnits || undefined, maxUnits: maxUnits || undefined, search: searchTerm || undefined
        }),
        api.getGemeenten(),
        api.getStatuses(),
        api.getStats()
      ])
      setProjects(projectsData)
      setGemeenten(gemeentenData)
      setStatuses(statusesData)
      setStats(statsData)
    } catch (err) {
      setError('Kon data niet laden.')
    }
    setLoading(false)
  }, [selectedGemeente, selectedType, selectedStatus, selectedWoningType, bouwOnly, minUnits, maxUnits, searchTerm])

  useEffect(() => { fetchData() }, [])

  const filterTimeoutRef = useRef(null)
  useEffect(() => {
    if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current)
    filterTimeoutRef.current = setTimeout(() => fetchData(), 300)
    return () => { if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current) }
  }, [selectedGemeente, selectedType, selectedStatus, selectedWoningType, bouwOnly, minUnits, maxUnits, searchTerm])

  const filteredProjects = useMemo(() => {
    let result = projects
    if (selectedActieType !== 'all') result = result.filter(p => p.actie_type === selectedActieType)
    if (showFavoritesOnly) result = result.filter(p => favoriteIds.has(p.id))
    return result
  }, [projects, selectedActieType, showFavoritesOnly, favoriteIds])

  const totalUnits = useMemo(() => filteredProjects.reduce((sum, p) => sum + (p.aantal_units || 0), 0), [filteredProjects])

  const handleProjectClick = useCallback((project) => {
    setSelectedProject(project)
    if (project.lat && project.lng) {
      setMapCenter([project.lat, project.lng])
      setMapZoom(14)
    }
  }, [])

  const toggleFavorite = useCallback(async (projectId) => {
    if (!user) { setShowLoginModal(true); return }
    const isFav = favoriteIds.has(projectId)
    const newFavs = new Set(favoriteIds)
    if (isFav) {
      newFavs.delete(projectId)
      await api.removeFavorite(projectId)
      showToast('Verwijderd uit favorieten')
    } else {
      newFavs.add(projectId)
      await api.addFavorite(projectId)
      showToast('Toegevoegd aan favorieten')
    }
    setFavoriteIds(newFavs)
  }, [user, favoriteIds, showToast])

  const handleSaveFilter = useCallback(async (name, filters, isDefault) => {
    try {
      const saved = await api.saveFilter(name, filters, isDefault)
      setSavedFilters(prev => [...prev, saved])
      showToast('Filter opgeslagen')
    } catch (err) {
      showToast('Kon filter niet opslaan', 'error')
    }
  }, [showToast])

  const handleDeleteFilter = useCallback(async (filterId) => {
    await api.deleteFilter(filterId)
    setSavedFilters(prev => prev.filter(f => f.id !== filterId))
    showToast('Filter verwijderd')
  }, [showToast])

  const handleCreateSubscription = useCallback(async (data) => {
    try {
      const result = await api.createSubscription(data)
      if (result.success) {
        setSubscriptions(prev => [...prev, result.subscription])
        showToast('Notificatie aangemaakt!')
        setDrawingPolygon(null)
      }
    } catch (err) {
      showToast('Kon notificatie niet aanmaken', 'error')
    }
  }, [showToast])

  const handleDeleteSubscription = useCallback(async (id) => {
    await api.deleteSubscription(id)
    setSubscriptions(prev => prev.filter(s => s.id !== id))
    showToast('Notificatie verwijderd')
  }, [showToast])

  const handleLogout = useCallback(async () => {
    await api.logout()
    setUser(null)
    showToast('Uitgelogd')
  }, [showToast])

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2.5 flex items-center justify-between shadow-lg z-10">
        <div className="flex items-center gap-2">
          <MapPin size={22} className="text-blue-400" />
          <div>
            <h1 className="text-lg font-bold leading-tight flex items-center gap-2">
              Omgevingsloket Monitor
              <span className="text-xs bg-green-500 px-1.5 py-0.5 rounded font-medium">LIVE</span>
            </h1>
            <p className="text-xs text-gray-400">Openbare onderzoeken & vergunningen in Vlaanderen</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Vernieuwen
          </button>
          {user ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-300 flex items-center gap-1"><User size={14} />{user.email}</span>
              <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm">
                <LogOut size={14} /> Uitloggen
              </button>
            </div>
          ) : (
            <button onClick={() => setShowLoginModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm">
              <LogIn size={14} /> Inloggen
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="bg-red-100 border-b border-red-200 px-4 py-2 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-96 bg-white shadow-lg flex flex-col overflow-hidden">
          <div className="p-3 border-b space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1"><Filter size={14} /> FILTERS</h2>
              {user && (
                <div className="flex gap-1">
                  <button onClick={() => setShowSaveFilterModal(true)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    <Save size={12} /> Opslaan
                  </button>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-0.5">Gemeente</label>
                <select value={selectedGemeente} onChange={e => setSelectedGemeente(e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-blue-500">
                  <option value="all">Alle gemeenten</option>
                  {gemeenten.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-0.5">Vergunningstype</label>
                <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-blue-500">
                  <option value="all">Alle types</option>
                  {Object.entries(typeConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-0.5">Status</label>
              <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-blue-500">
                <option value="all">Alle statussen</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-0.5">Zoeken</label>
              <div className="relative">
                <Search size={12} className="absolute left-2 top-2 text-gray-400" />
                <input type="text" placeholder="Adres, omschrijving, dossiernr..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-7 pr-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          <div className="border-b">
            <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className="w-full px-3 py-2 flex items-center justify-between text-xs font-semibold text-gray-500 uppercase hover:bg-gray-50">
              <span className="flex items-center gap-1"><Settings size={11} /> Geavanceerde Filters</span>
              {showAdvancedFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            
            {showAdvancedFilters && (
              <div className="px-3 pb-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Woning Type</label>
                    <select value={selectedWoningType} onChange={e => setSelectedWoningType(e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs">
                      <option value="all">Alle types</option>
                      {Object.entries(woningTypeConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Actie Type</label>
                    <select value={selectedActieType} onChange={e => setSelectedActieType(e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs">
                      <option value="all">Alle acties</option>
                      {Object.entries(actieTypeConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Aantal Units</label>
                  <div className="flex gap-2 items-center">
                    <input type="number" placeholder="Min" value={minUnits} onChange={e => setMinUnits(e.target.value)} className="w-20 px-2 py-1.5 border rounded text-xs" />
                    <span className="text-gray-400">—</span>
                    <input type="number" placeholder="Max" value={maxUnits} onChange={e => setMaxUnits(e.target.value)} className="w-20 px-2 py-1.5 border rounded text-xs" />
                  </div>
                </div>
                
                <div className="flex items-center gap-2 pt-2 border-t mt-2">
                  <input 
                    type="checkbox" 
                    id="bouwOnly" 
                    checked={bouwOnly} 
                    onChange={e => setBouwOnly(e.target.checked)} 
                    className="rounded text-blue-600" 
                  />
                  <label htmlFor="bouwOnly" className="text-xs text-gray-600 cursor-pointer">
                    Alleen bouw-gerelateerd <span className="text-gray-400">(verberg groen/landschap)</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Notifications section */}
          <div className="border-b px-3 py-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><Bell size={11} /> Notificaties</h3>
              <button onClick={() => user ? setShowSubscriptionModal(true) : setShowLoginModal(true)} className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1">
                <Plus size={12} /> Nieuw
              </button>
            </div>
            {subscriptions.length > 0 ? (
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {subscriptions.map(sub => (
                  <div key={sub.id} className="flex items-center justify-between bg-amber-50 px-2 py-1 rounded text-xs">
                    <span className="truncate flex-1">{sub.name || 'Notificatie'}</span>
                    <button onClick={() => handleDeleteSubscription(sub.id)} className="text-red-400 hover:text-red-600 ml-2"><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">Geen notificaties ingesteld</p>
            )}
            {drawingPolygon && drawingPolygon.length > 0 && (
              <div className="mt-2 bg-blue-50 p-2 rounded">
                <p className="text-xs text-blue-700 flex items-center gap-1"><PenTool size={12} /> Gebied getekend</p>
                <button onClick={() => setShowSubscriptionModal(true)} className="mt-1 text-xs bg-amber-500 text-white px-2 py-1 rounded hover:bg-amber-600">
                  Notificatie maken voor dit gebied
                </button>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="px-3 py-2 border-b bg-gray-50 grid grid-cols-4 gap-2 text-center">
            <div><div className="text-lg font-bold text-blue-600">{filteredProjects.length}</div><div className="text-xs text-gray-500">resultaten</div></div>
            <div><div className="text-lg font-bold text-green-600">{stats.newToday || 0}</div><div className="text-xs text-gray-500">nieuw</div></div>
            <div><div className="text-lg font-bold text-purple-600">{totalUnits}</div><div className="text-xs text-gray-500">units</div></div>
            <div><div className="text-lg font-bold text-amber-600">{favoriteIds.size}</div><div className="text-xs text-gray-500">favorieten</div></div>
          </div>

          {/* Projects list */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {filteredProjects.slice(0, 200).map(p => {
              const tc = typeConfig[p.type] || typeConfig.stedenbouwkundig
              const wtc = woningTypeConfig[p.woning_type] || woningTypeConfig.onbekend
              const isFav = favoriteIds.has(p.id)
              
              return (
                <div key={p.id} className={`p-2 border rounded-lg transition-all hover:border-blue-400 hover:shadow-sm ${selectedProject?.id === p.id ? 'border-blue-500 bg-blue-50' : 'bg-white'}`}>
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex gap-1 flex-wrap cursor-pointer" onClick={() => handleProjectClick(p)}>
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: tc.bg, color: tc.color }}>{tc.label}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: wtc.color + '20', color: wtc.color }}>{wtc.label}</span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); toggleFavorite(p.id); }} className={`p-1 rounded transition-colors ${isFav ? 'text-amber-500 hover:text-amber-600' : 'text-gray-300 hover:text-amber-400'}`} title={isFav ? 'Verwijder uit favorieten' : 'Voeg toe aan favorieten'}>
                      <Star size={16} className={isFav ? 'fill-current' : ''} />
                    </button>
                  </div>
                  
                  <h4 className="text-xs font-medium text-gray-800 mb-1 line-clamp-2 cursor-pointer" onClick={() => handleProjectClick(p)}>{p.title}</h4>
                  
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="flex items-center gap-0.5"><MapPin size={10} />{p.gemeente}</span>
                    {p.start_datum && <span className="flex items-center gap-0.5"><Calendar size={10} />{p.start_datum}</span>}
                    {p.aantal_units && <span className="flex items-center gap-0.5 text-blue-600 font-medium"><Hash size={10} />{p.aantal_units}</span>}
                  </div>
                </div>
              )
            })}
            {filteredProjects.length > 200 && (
              <p className="text-center text-xs text-gray-400 py-2">+ {filteredProjects.length - 200} meer resultaten</p>
            )}
            {filteredProjects.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-400">
                <Search size={32} className="mx-auto mb-2 opacity-50" />
                <p>Geen resultaten</p>
              </div>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer center={mapCenter} zoom={mapZoom} className="h-full w-full" zoomControl={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
            <MapController center={mapCenter} zoom={mapZoom} />
            <DrawControl 
              polygon={drawingPolygon} 
              onPolygonCreated={setDrawingPolygon} 
              onPolygonDeleted={() => setDrawingPolygon(null)} 
            />
            
            {filteredProjects.filter(p => p.lat && p.lng).map(p => {
              const wtc = woningTypeConfig[p.woning_type] || woningTypeConfig.onbekend
              const isSelected = selectedProject?.id === p.id
              const isFav = favoriteIds.has(p.id)
              let radius = 6
              if (p.aantal_units) {
                if (p.aantal_units >= 20) radius = 14
                else if (p.aantal_units >= 10) radius = 11
                else if (p.aantal_units >= 5) radius = 8
              }
              
              return (
                <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={isSelected ? radius + 4 : radius} fillColor={isFav ? '#f59e0b' : wtc.color} color={isSelected ? '#000' : isFav ? '#d97706' : '#fff'} weight={isSelected ? 3 : 2} fillOpacity={0.8} eventHandlers={{ click: () => handleProjectClick(p) }}>
                  <Popup>
                    <h4>{p.title}</h4>
                    <p><strong>Type:</strong> {typeConfig[p.type]?.label || p.type}</p>
                    <p><strong>Status:</strong> {p.status}</p>
                    <p><strong>Gemeente:</strong> {p.gemeente}</p>
                    <a href={`https://omgevingsloketinzage.omgeving.vlaanderen.be/?search=${p.id}`} target="_blank" rel="noopener noreferrer">Bekijk op Omgevingsloket →</a>
                  </Popup>
                </CircleMarker>
              )
            })}
          </MapContainer>

          {/* Legend */}
          <div className="absolute top-3 right-3 bg-white/95 rounded-lg shadow-lg p-3 text-xs z-[1000]">
            <h4 className="font-semibold mb-2 text-gray-700">Legenda</h4>
            <div className="space-y-1">
              {Object.entries(woningTypeConfig).filter(([k]) => k !== 'onbekend').slice(0, 5).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: v.color }}></span><span className="text-gray-600">{v.label}</span></div>
              ))}
              <div className="flex items-center gap-1.5 pt-1 border-t mt-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span><span className="text-gray-600">Favoriet</span></div>
            </div>
          </div>

          {selectedProject && (
            <div className="absolute bottom-3 left-3 right-3 bg-white rounded-lg shadow-lg p-3 max-w-lg z-[1000]">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: (typeConfig[selectedProject.type] || typeConfig.stedenbouwkundig).bg, color: (typeConfig[selectedProject.type] || typeConfig.stedenbouwkundig).color }}>
                  {(typeConfig[selectedProject.type] || typeConfig.stedenbouwkundig).label}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => toggleFavorite(selectedProject.id)} className={`p-1.5 rounded transition-colors ${favoriteIds.has(selectedProject.id) ? 'text-amber-500 hover:text-amber-600' : 'text-gray-400 hover:text-amber-400'}`}>
                    <Star size={18} className={favoriteIds.has(selectedProject.id) ? 'fill-current' : ''} />
                  </button>
                  <button onClick={() => setSelectedProject(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
                    <X size={18} />
                  </button>
                </div>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">{selectedProject.title}</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 mb-2">
                <p><strong>Status:</strong> {selectedProject.status}</p>
                <p><strong>Gemeente:</strong> {selectedProject.gemeente}</p>
                {selectedProject.adres && <p><strong>Adres:</strong> {selectedProject.adres}</p>}
                {selectedProject.aantal_units && <p><strong>Units:</strong> <span className="text-blue-600 font-semibold">{selectedProject.aantal_units}</span></p>}
                <p><strong>Dossier:</strong> {selectedProject.id}</p>
              </div>
              <a href={`https://omgevingsloketinzage.omgeving.vlaanderen.be/?search=${selectedProject.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 text-xs hover:underline">
                <ExternalLink size={12} /> Bekijk volledig dossier
              </a>
            </div>
          )}
        </div>
      </div>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <SaveFilterModal isOpen={showSaveFilterModal} onClose={() => setShowSaveFilterModal(false)} filters={currentFilters} onSave={handleSaveFilter} />
      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} user={user} polygon={drawingPolygon} statuses={statuses} onSave={handleCreateSubscription} />

      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg text-sm text-white toast-enter z-[2000] ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
          {toast.message}
        </div>
      )}
    </div>
  )
}
